import type { EventDetailSlide } from '@/types/event';
import { formatEventDateRange } from '@/utils/event';
import clsx from 'clsx';
import { useCallback, useRef } from 'react';

interface EventDetailGridHorizontalProps {
    slideItems: (EventDetailSlide & { tabIndex?: number })[];
}

export default function EventDetailGridHorizontal({ slideItems }: EventDetailGridHorizontalProps) {
    const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const generateArialLabel = (item: EventDetailSlide) => {
        const formattedDate = formatEventDateRange(item?.cntsStDtm, item?.cntsEndDtm);
        return item.cntsBadgeNm + ' ' + item.cntsHeader + ' ' + (item?.brndNm ?? '') + ' ' + item.cntsNm + ' ' + (item?.evhalCdNm ?? '') + ' ' + formattedDate;
    };

    const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.currentTarget as HTMLElement;
        if (document.activeElement !== target) {
            target.focus();
        }
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.currentTarget as HTMLElement;

        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
            focusTimeoutRef.current = null;
        }

        if (document.activeElement !== target) {
            focusTimeoutRef.current = setTimeout(() => {
                target.focus();
                focusTimeoutRef.current = null;
            }, 0);
        }
    }, []);

    return (
        <div className="grid grid-cols-5 gap-[calc(12*var(--vw-unit))]">
            {Array.isArray(slideItems) &&
                slideItems.map((item) => {
                    return (
                        <div
                            key={item.cntsNo}
                            tabIndex={item.tabIndex ?? 4}
                            aria-label={generateArialLabel(item)}
                            data-interactive
                            className="group contrast:border-[calc(1*var(--vh-unit))] contrast:border-primary-white focus-border rounded-[calc(8*var(--vh-unit))] border-[calc(1*var(--vh-unit))] border-[var(--color-primary-black-light)]"
                            onClick={handleClick}
                            onMouseDown={handleMouseDown}
                        >
                            <div className="fullscreen:h-[calc(278*var(--vh-unit))] h-[calc(220*var(--vh-unit))] overflow-hidden rounded-t-[calc(8*var(--vh-unit))]">
                                <img src={item.cntsImageUrl} alt={item.cntsHeader} className="h-full w-full object-cover" loading="lazy" />
                            </div>

                            <div className="bg-bg-white flex h-[calc(178*var(--vh-unit))] flex-col gap-[calc(8*var(--vh-unit))] rounded-b-[calc(8*var(--vh-unit))] px-[calc(22*var(--vw-unit))] py-[calc(20*var(--vh-unit))] text-left">
                                <div className="contrast:border-[calc(1*var(--vh-unit))] contrast:border-primary-white text-text-sub01 w-fit rounded-[calc(14*var(--vw-unit))] bg-[var(--color-eaeaea-to-black-222)] px-[calc(14*var(--vw-unit))] text-[calc(14*var(--vh-unit))] font-medium">
                                    {item.cntsBadgeNm}
                                </div>
                                <div>
                                    <div className="text-text-main01 w-[calc(236*var(--vw-unit))] truncate text-[calc(20*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-bold">
                                        {item.cntsHeader}
                                    </div>
                                    <div className="text-text-main01 w-[calc(236*var(--vw-unit))] truncate text-[calc(20*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-bold">
                                        {item?.cntsNm ?? ''}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-[calc(4*var(--vh-unit))]">
                                    <div className="text-text-sub01 w-[calc(236*var(--vw-unit))] h-[calc(18*var(--vw-unit))] truncate text-[calc(16*var(--vh-unit))] leading-[calc(18*var(--vh-unit))] font-medium">
                                        {item.evhalCdNm ?? ' '}
                                    </div>
                                    <div className="text-text-sub01 text-[calc(16*var(--vh-unit))] leading-[calc(18*var(--vh-unit))] font-medium">
                                        {item.cntsDtm}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    );
}
